import {
  ReactNode,
  RefObject,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { createPortal } from "react-dom";

interface PowerSlidePopoverProps {
  anchorRef: RefObject<HTMLElement>;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  ariaLabel: string;
  role?: "menu" | "dialog" | "listbox";
  /** Match the anchor's width, as a select-style dropdown should. */
  matchAnchorWidth?: boolean;
  /** Preferred side; flipped automatically when there is no room. */
  prefer?: "below" | "above";
}

const VIEWPORT_MARGIN = 8;

/**
 * A popover rendered into document.body.
 *
 * Everything this feature pops open lives inside something that clips: the
 * slide rail is an `overflow-x-auto` scroller, and the properties column is an
 * `overflow-y-auto` body full of `overflow-hidden` sections. An absolutely
 * positioned menu is cut off by all three, so position is computed from the
 * anchor's viewport rect and the content is portalled out.
 */
export default function PowerSlidePopover(props: PowerSlidePopoverProps) {
  const {
    anchorRef,
    isOpen,
    onClose,
    children,
    ariaLabel,
    role = "dialog",
    matchAnchorWidth,
    prefer = "below",
  } = props;
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
    width?: number;
  }>();

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!isOpen || !anchor) return;

    const place = () => {
      const rect = anchor.getBoundingClientRect();
      const height = element?.offsetHeight ?? 0;
      const width = element?.offsetWidth ?? rect.width;

      const roomBelow = window.innerHeight - rect.bottom;
      const openAbove =
        prefer === "above"
          ? rect.top > height + VIEWPORT_MARGIN
          : roomBelow < height + VIEWPORT_MARGIN &&
            rect.top > roomBelow;

      const top = openAbove ? rect.top - height - 4 : rect.bottom + 4;
      const left = Math.max(
        VIEWPORT_MARGIN,
        Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN)
      );

      setPosition({
        left,
        top: Math.max(VIEWPORT_MARGIN, top),
        ...(matchAnchorWidth && { width: rect.width }),
      });
    };

    place();
    // Reposition rather than drift: the rail scrolls and the column scrolls.
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [isOpen, anchorRef, element, matchAnchorWidth, prefer]);

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      // The anchor toggles on its own; closing here too would reopen it.
      if (element?.contains(target) || anchorRef.current?.contains(target)) {
        return;
      }
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose, element, anchorRef]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={setElement}
      role={role}
      aria-label={ariaLabel}
      style={{
        position: "fixed",
        left: position?.left ?? 0,
        top: position?.top ?? 0,
        width: position?.width,
        // Hidden until measured, so it never flashes at the wrong place.
        visibility: position ? "visible" : "hidden",
        zIndex: 12000,
      }}
      className="rounded-[9px] border border-solid border-creator-border bg-creator-surface shadow-lg"
    >
      {children}
    </div>,
    document.body
  );
}
